import { useEffect, useState, useMemo } from "react";
import { addTicket, fetchTickets, updateTicket } from "./service";
import { Notification } from "./style";
import { getRelativeTime, formatFullDateTime } from "./DateHelpers";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
  Container,
  Row,
  Col,
} from "reactstrap";
import { ProjectButton } from "../project/style";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { createUserProject } from "../project/service";
import { TicketDetailPane } from "../tickets";

const Support = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderIdentifier, setOrderIdentifier] = useState(null);
  const [subject, setSubject] = useState("");
  const [eta, setEta] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [today, setToday] = useState("");
  const [ticketModal, setTicketModal] = useState(false);
  const [ticketModalInfo, setTicketModalInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });
  const statusFilterKeyMapper = {
    open: ["open"],
    closed: ["closed"],
    production: ["production"],
  };

  useEffect(() => {
    getTickets();
    setToday(new Date().toISOString().split("T")[0]);
  }, []);

  // Filter and sort tickets
  const filteredAndSortedTickets = useMemo(() => {
    let filterTickets = tickets;

    // Apply status filter
    if (statusFilterKeyMapper[statusFilter]) {
      filterTickets = filterTickets.filter((ticket) =>
        statusFilterKeyMapper[statusFilter].includes(ticket.status)
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filterTickets = [...filterTickets].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === "logged_at" || sortConfig.key === "eta") {
          // Date sorting
          aVal = aVal ? new Date(aVal).getTime() : 0;
          bVal = bVal ? new Date(bVal).getTime() : 0;
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        if (sortConfig.key === "cost") {
          // Numeric sorting
          aVal = aVal || 0;
          bVal = bVal || 0;
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        if (sortConfig.key === "status") {
          // String sorting
          aVal = (aVal || "").toString().toLowerCase();
          bVal = (bVal || "").toString().toLowerCase();
          if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }

        return 0;
      });
    }

    return filterTickets;
  }, [tickets, statusFilter, sortConfig]);

  const getTickets = () => {
    setIsLoading(true);

    fetchTickets()
      .then(({ tickets, orders }) => {
        setTickets(tickets);
        setOrders(orders);
      })
      .catch((err) => {
        setIsNotificationVisible(true);
        setNotification({ type: "error", message: err.error });
      })
      .finally(() => setIsLoading(false));
  };

  const createTicket = ({ orderIdentifier, subject }) => {
    if (orderIdentifier && subject) {
      // Find the order by identifier to get its ID
      const order = orders.find((o) => o.identifier === orderIdentifier);
      if (!order || !order.id) {
        alert("Order not found");
        return;
      }

      setIsLoading(true);

      addTicket({ orderId: order.id, subject })
        .then(({ ticket }) => {
          setTickets((prevTickets) => [...prevTickets, ticket]);
          closeTicketModal();
        })
        .catch((err) => {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: err.error });
        })
        .finally(() => setIsLoading(false));
    } else alert("Please fill the details");
  };

  const editTicket = ({ id, key, value }) => {
    if (id && key && value) {
      setIsLoading(true);

      updateTicket({ id, key, value })
        .then(({ ticket }) => {
          const newTickets = tickets;
          const oldTicket = newTickets.find((e) => e.id == ticket.id);
          Object.assign(oldTicket, ticket);
          setTickets(newTickets);

          closeTicketModal();
        })
        .catch((err) => {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: err.error });
        })
        .finally(() => setIsLoading(false));
    } else alert("Please fill the details");
  };

  const createSupportPanelsProject = (ticket) => {
    setIsLoading(true);

    createUserProject({
      name: `Support Panels Project - ${ticket.id}`,
      version: 2,
      ticket_id: ticket.id,
    })
      .then((response) => {
        setIsNotificationVisible(true);
        setNotification({
          type: "success",
          message: response.message || `Project created successfully for ticket ${ticket.id}`,
        });
        // Navigate to the new project
        navigate(`/projects/${response.project_id}`);
      })
      .catch((err) => {
        setIsNotificationVisible(true);
        setNotification({
          type: "error",
          message: err.error || "Failed to create project",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const canCreateSupportProject = (ticket) => {
    return String(ticket?.order_status || "").toLowerCase() === "closed";
  };

  const closeTicketModal = () => {
    setTicketModal(false);
    setTicketModalInfo(null);
    setOrderIdentifier(null);
    setSubject("");
    setEta("");
  };

  const formatDate = (str) => {
    const date = new Date(str);

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };


  const getColorForEta = (ticket) => {
    if (ticket.status === "closed") return "subtle-gray";
    else {
      if (ticket.eta && new Date(ticket.eta) < new Date()) return "text-danger";
      else if (!ticket.eta) return "text-warning";
      else return "";
    }
  };

  const handleTicketClick = (ticket) => {
    console.log("Ticket clicked:", ticket); // Debug log
    setSelectedTicketId(ticket.id);
    const orderId = ticket.order?.id;
    console.log("Setting order ID:", orderId); // Debug log
    setSelectedOrderId(orderId);
  };

  const handleTicketUpdate = () => {
    getTickets(); // Refresh the tickets list
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "danger",
      analysed: "warning",
      production: "info",
      closed: "success",
      invalid: "secondary",
    };
    return colors[status] || "secondary";
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return {
        key,
        direction: "asc",
      };
    });
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  return (
    <>
      {isNotificationVisible && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={() => setIsNotificationVisible(false)}
        >
          {notification.message}
        </Notification>
      )}
      <div style={{ minHeight: "100vh", backgroundColor: "#eef2f6" }}>
        <Container className="p-4 w-100" style={{ maxWidth: "100%" }}>
          <Row className="mb-4">
            <Col>
              <h3 className="fw-bold">
                <i className="fas fa-headset text-primary me-2"></i>
                Support
              </h3>
            </Col>
          </Row>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner size="sm">
                Loading...
              </Spinner>
            </div>
          ) : (
            <Row className="border p-3 bg-white rounded">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex gap-2">
                    <Button
                      color={statusFilter === "open" ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => setStatusFilter("open")}
                      className="rounded-pill"
                    >
                      Open
                    </Button>
                    <Button
                      color={statusFilter === "production" ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => setStatusFilter("production")}
                      className="rounded-pill"
                    >
                      Production
                    </Button>
                    <Button
                      color={statusFilter === "closed" ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => setStatusFilter("closed")}
                      className="rounded-pill"
                    >
                      Closed
                    </Button>
                  </div>
                </div>
                <ProjectButton
                  onClick={() => {
                    setTicketModal(true);
                    setTicketModalInfo({
                      action: "create",
                    });
                  }}
                  className="rounded-1"
                >
                  Create Ticket
                </ProjectButton>
              </div>
              <Table
              bordered
              size="sm"
              className="align-middle mb-0 table-striped"
              style={{ fontSize: "0.9rem" }}
            >
              <thead className="bg-secondary text-white">
                <tr>
                  <th
                    className="bg-secondary text-white"
                    role="button"
                    onClick={() => handleSort("logged_at")}
                    style={{ cursor: "pointer" }}
                  >
                    Date {renderSortIcon("logged_at")}
                  </th>
                  <th className="bg-secondary text-white">Id</th>
                  <th className="bg-secondary text-white">Customer</th>
                  <th className="bg-secondary text-white">Order</th>
                  <th className="bg-secondary text-white">Parts</th>
                  <th className="bg-secondary text-white">Issue</th>
                  <th
                    className="bg-secondary text-white"
                    role="button"
                    onClick={() => handleSort("cost")}
                    style={{ cursor: "pointer" }}
                  >
                    Cost {renderSortIcon("cost")}
                  </th>
                  <th
                    className="bg-secondary text-white"
                    role="button"
                    onClick={() => handleSort("eta")}
                    style={{ cursor: "pointer" }}
                  >
                    ETA {renderSortIcon("eta")}
                  </th>
                  <th
                    className="bg-secondary text-white"
                    role="button"
                    onClick={() => handleSort("status")}
                    style={{ cursor: "pointer" }}
                  >
                    Status {renderSortIcon("status")}
                  </th>
                  <th className="bg-secondary text-white" style={{ width: "80px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedTickets.map((ticket, index) => (
                  <tr
                    key={index}
                    onClick={() => handleTicketClick(ticket)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      {ticket.logged_at ? (
                        <>
                          <span id={`date-tooltip-${ticket.id}`}>
                            {getRelativeTime(ticket.logged_at)}
                          </span>
                          <UncontrolledTooltip target={`date-tooltip-${ticket.id}`}>
                            {formatFullDateTime(ticket.logged_at)}
                          </UncontrolledTooltip>
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {ticket.identifier}
                      {(ticket.impacted_parts_count > 0 || (ticket.impacted_parts && ticket.impacted_parts.length > 0)) && (
                        <Badge color="info" pill className="ms-2" style={{ fontSize: "0.7rem" }}>
                          {ticket.impacted_parts_count || ticket.impacted_parts?.length || 0} parts
                        </Badge>
                      )}
                    </td>
                    <td>{ticket.org_name}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="text-dark">
                          {ticket.project?.name}
                        </span>
                      </div>
                    </td>
                    <td>
                      {ticket.impacted_parts_count
                        ? `${ticket.impacted_parts_count} ${ticket.impacted_parts_count === 1 ? "part" : "parts"}`
                        : "-"}
                    </td>
                    <td>{ticket.subject}</td>
                    <td>
                      {ticket.cost ? `₹${ticket.cost}` : "-"}
                    </td>
                    <td>
                      <span className={getColorForEta(ticket)}>
                        {(ticket.eta && formatDate(ticket.eta)) || "Not set"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Badge color={getStatusBadgeColor(ticket.status)} className="text-capitalize">
                          {ticket.status}
                        </Badge>
                        {ticket.status === "closed" && (
                          <span className="text-muted small">
                            {formatDate(ticket.closed_on)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <UncontrolledDropdown>
                        <DropdownToggle
                          className="border-0 p-1 text-dark"
                          style={{ background: "transparent" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fa-solid fa-ellipsis-vertical"></i>
                        </DropdownToggle>
                        <DropdownMenu>
                          {ticket.support_project ? (
                            <DropdownItem
                              tag="a"
                              href={`/projects/${ticket.support_project}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Support Project
                            </DropdownItem>
                          ) : canCreateSupportProject(ticket) ? (
                            <DropdownItem
                              onClick={() => createSupportPanelsProject(ticket)}
                              disabled={isLoading}
                            >
                              Create Support Project
                            </DropdownItem>
                          ) : (
                            <>
                              <span id={`create-support-project-disabled-${ticket.id}`}>
                                <DropdownItem disabled>
                                  Create Support Project
                                </DropdownItem>
                              </span>
                              <UncontrolledTooltip
                                target={`create-support-project-disabled-${ticket.id}`}
                                placement="left"
                              >
                                Support project can be created only when order status is Closed.
                              </UncontrolledTooltip>
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
              {filteredAndSortedTickets.length === 0 && (
                <h5 className="mt-4 text-center">No Tickets Found</h5>
              )}
            </Row>
          )}
        </Container>
      </div>

      <Modal isOpen={ticketModal} toggle={closeTicketModal}>
        {ticketModalInfo && (
          <>
            <ModalHeader toggle={closeTicketModal}>
              {ticketModalInfo.action === "create"
                ? "Create Ticket"
                : ticketModalInfo.key === "eta"
                  ? "ETA Update"
                  : "Close Ticket"}
            </ModalHeader>
            <ModalBody className="d-flex flex-column row-gap-3">
              {ticketModalInfo.action === "create" ? (
                <>
                  <div>
                    <p className="m-0 text-secondary fw-bold">
                      Order Identifier
                    </p>
                    <Select
                      className="mt-1"
                      options={orders.map((order) => {
                        const label = order.context
                          ? `(${order.context[0].toUpperCase()})`
                          : "";

                        return {
                          label: `${order.org_name} / ${order.project_name} - ${order.identifier} ${label}`,
                          value: order.identifier,
                        };
                      })}
                      onChange={(e) => setOrderIdentifier(e.value)}
                    />
                  </div>
                  <div>
                    <p className="m-0 text-secondary fw-bold">Subject</p>
                    <Input
                      type="text"
                      id="subject"
                      className="mt-1 py-2"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>{" "}
                </>
              ) : ticketModalInfo.key === "eta" ? (
                <div>
                  <p className="m-0 text-secondary fw-bold">ETA</p>
                  <Input
                    type="date"
                    id="date"
                    className="mt-1 py-2"
                    value={eta}
                    onChange={(e) => setEta(e.target.value)}
                    min={today}
                  />
                </div>
              ) : (
                <p className="m-0">
                  Are you sure you want to close the ticket?
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="primary"
                onClick={() => {
                  if (ticketModalInfo.action === "create")
                    createTicket({ orderIdentifier, subject });
                  else if (ticketModalInfo.key === "eta")
                    editTicket({
                      id: ticketModalInfo.id,
                      key: ticketModalInfo.key,
                      value: eta,
                    });
                  else if (ticketModalInfo.key === "status")
                    editTicket({
                      id: ticketModalInfo.id,
                      key: ticketModalInfo.key,
                      value: "closed",
                    });
                }}
              >
                Submit
              </Button>
              <Button color="secondary" onClick={closeTicketModal}>
                Cancel
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Ticket Detail Pane - slides in from right */}
      {console.log("Rendering TicketDetailPane with:", { selectedTicketId, selectedOrderId })}
      <TicketDetailPane
        ticketId={selectedTicketId}
        orderId={selectedOrderId}
        onClose={() => {
          console.log("Closing ticket detail pane");
          setSelectedTicketId(null);
          setSelectedOrderId(null);
        }}
        onUpdate={handleTicketUpdate}
      />
    </>
  );
};

export default Support;
